// app/frontend/pages/NewConfirmation.tsx
import { useState } from 'react';
import { Head } from '@inertiajs/react';
import { useForm } from '@mantine/form';
import {
  Container,
  Title,
  TextInput,
  Paper,
  Button,
  Stack,
  Box,
  MantineProvider,
  createTheme,
  MantineTheme,
  Anchor,
} from '@mantine/core';
import { Notifications, showNotification } from '@mantine/notifications';
import { IconMail, IconAlertCircle, IconCheck } from '@tabler/icons-react';
import '../styles/Login-form.css';

const theme = createTheme({
  primaryColor: 'orange',
  colors: {
    orange: [
      '#FEF5ED',
      '#FDE3D1',
      '#FCB693',
      '#FA9D73',
      '#F87F4D',
      '#F66A33',
      '#EA580C', // Accent color
      '#C44B0A',
      '#9E3D08',
      '#783006',
    ],
  },
  fontFamily: 'Poppins, sans-serif',
  headings: {
    fontFamily: 'Poppins, sans-serif',
  },
});

export default function NewConfirmation() {
  const [isLoading, setIsLoading] = useState(false);

  // Set up the form with an email field and real-time validation
  const form = useForm({
    initialValues: {
      email: '',
    },
    validate: {
      email: (value) => (/^\S+@\S+$/.test(value) ? null : 'Please enter a valid email'),
    },
    validateInputOnChange: true,
  });

  const handleSubmit = async (values: typeof form.values) => {
    setIsLoading(true);
    const csrfToken = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content;

    try {
      const formData = new FormData();
      formData.append('user[email]', values.email);

      const response = await fetch('/confirmation', {
        method: 'POST',
        headers: {
          'X-CSRF-Token': csrfToken || '',
        },
        credentials: 'include',
        body: formData,
      });

      if (response.ok) {
        showNotification({
          color: 'green',
          title: 'Instructions Sent',
          message: 'Confirmation instructions have been sent to your email.',
          icon: <IconCheck />,
          autoClose: 3000,
        });
      } else {
        const data = await response.json().catch(() => ({}));
        showNotification({
          color: 'red',
          title: 'Error',
          message: data.error || 'Failed to send confirmation instructions.',
          icon: <IconAlertCircle />,
          autoClose: 5000,
        });
      }
    } catch (error) {
      showNotification({
        color: 'red',
        title: 'Error',
        message: 'An error occurred. Please try again later.',
        icon: <IconAlertCircle />,
        autoClose: 5000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <MantineProvider theme={theme}>
      <Notifications position="top-right" />
      <Head title="Resend Confirmation Instructions" />
      <Container size="xs" mt="lg" style={{ minHeight: '75vh' }}>
        <Box>
          <Paper shadow="md" p={30} radius="md" styles={{ root: { backgroundColor: '#75757530' } }}>
            <Title order={2} ta="center" mb="md" c="white">
              Resend confirmation instructions
            </Title>
            <form onSubmit={form.onSubmit(handleSubmit)}>
              <Stack>
                <TextInput
                  label="Email"
                  placeholder="your@email.com"
                  required
                  disabled={isLoading}
                  {...form.getInputProps('email')}
                />
                <Button type="submit" fullWidth size="lg" loading={isLoading} className="button-orange">
                  Resend confirmation instructions
                </Button>
              </Stack>
            </form>
            <Stack mt="md" gap="xs" className="login-links mt-4">
              <Anchor href="/login" className="text-gray-600 hover:text-orange-600 hover:underline">
                Have an account already? Login here!
              </Anchor>
              <Anchor href="/confirmation/new" className="text-gray-600 hover:text-orange-600 hover:underline">
                Didn't receive confirmation instructions?
              </Anchor>
            </Stack>
          </Paper>
        </Box>
      </Container>
    </MantineProvider>
  );
}
